import { TransferRail } from '@varo/graphql-schema-types';
import { Redirect, Stack, useNavigation, useRouter } from 'expo-router';
import { pressableAttributes } from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import useMatchedSubmitOutcome from 'features/feeBasedInstantTransfer/hooks/useMatchedSubmitOutcome';
import { submitSuccessAtom } from 'features/feeBasedInstantTransfer/state/submitOutcomeAtoms';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useRefreshSystemBanners } from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import usePreventBackNavigation from 'shared/hooks/usePreventBackNavigation/usePreventBackNavigation';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';

const TRANSFER_SUCCESS_SCREEN_KEY = 'instantTransferSuccess';

function TransferSuccessScreen() {
  const { t } = useTranslation('feeBasedInstantTransfer');
  const router = useRouter();
  const navigation = useNavigation();
  const { doneButtonTitle } = useDefaultErrorStrings();
  const success = useMatchedSubmitOutcome(submitSuccessAtom);
  const { refreshSystemBanners } = useRefreshSystemBanners();
  const isScheduled = success?.rail === TransferRail.Ach;
  const isListedInTransferHistory =
    isScheduled || success?.rail === TransferRail.Internal;

  useEffect(() => {
    refreshSystemBanners();
  }, [refreshSystemBanners]);

  const isLeavingRef = useRef(false);
  usePreventBackNavigation(Boolean(success), (action) => {
    if (isLeavingRef.current) {
      navigation.dispatch(action);
    }
  });

  const handleDone = () => {
    isLeavingRef.current = true;
    navigateBack();
  };

  if (!success) {
    return <Redirect href="/" />;
  }

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      <SuccessScreen
        visual="PLANE_A_ANIMATED"
        screenAnalyticsEventKey={TRANSFER_SUCCESS_SCREEN_KEY}
        eyebrowText={t('success.eyebrow')}
        titleText={t(
          isScheduled ? 'success.scheduledTitle' : 'success.transferredTitle',
          { amount: formatCurrency(success.amount) },
        )}
        content={
          success.transferSpeedMessage ? (
            <BodyBaseText numberOfLines={0} style={styles.message}>
              {success.transferSpeedMessage}
            </BodyBaseText>
          ) : null
        }
        primaryButton={{
          label: doneButtonTitle,
          onPress: handleDone,
          analyticsId: 'transferMoneySuccessDone',
          customAttributes: pressableAttributes('Done', 'primary'),
        }}
        secondaryButton={
          isListedInTransferHistory
            ? {
                label: t('success.seeTransfers'),
                analyticsId: 'transferMoneySuccessSeeTransfers',
                customAttributes: pressableAttributes(
                  'See My Transfers',
                  'secondary',
                ),
                onPress: () =>
                  router.replace('/move-money/transfer/transfer-history'),
              }
            : undefined
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  message: { textAlign: 'center' },
});

export default TransferSuccessScreen;
